import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { IClaim } from '../models/claim.model';
import { UploadType } from '../models/enums/upload-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ClaimFilesService {
  private readonly http = inject(HttpClient);
  private readonly httpWithoutInterceptors = new HttpClient(
    inject(HttpBackend)
  );

  private readonly baseUrl = `${environment.services.claims}/api/claims`;

  public uploadFileForClaim(
    claimId: number,
    file: File | File[],
    documentType: string,
    uploadType: UploadType,
    costTypeId?: number
  ): Observable<IClaim> {
    const files = Array.isArray(file) ? file : [file];
    const uploadRequest = files.map((singleFile) => ({
      fileName: singleFile.name,
      fileType: singleFile.type,
      fileSize: singleFile.size,
      yearly: false,
      component: 'claims',
      documentType,
      costTypeId,
    }));

    return this.http
      .post<IClaim>(
        `${this.baseUrl}/${claimId}/${uploadType}/upload`,
        uploadRequest
      )
      .pipe(
        switchMap((claim) => {
          const s3Uploads = files.map((singleFile) => {
            const uploadedFile = claim.files.find(
              (fileResponse) =>
                fileResponse.fileName === singleFile.name &&
                (costTypeId === undefined ||
                  fileResponse.costTypeId === costTypeId)
            );
            if (!uploadedFile?.preSignedUrl) {
              return throwError(
                () => new Error(`No pre-signed URL for ${singleFile.name}`)
              );
            }
            return this.uploadFileToS3(singleFile, uploadedFile.preSignedUrl);
          });
          return forkJoin(s3Uploads).pipe(map(() => claim));
        })
      );
  }

  public deleteFileFromClaim(
    claimId: number,
    fileId: string
  ): Observable<IClaim> {
    return this.http.delete<IClaim>(
      `${this.baseUrl}/${claimId}/file/${fileId}`
    );
  }

  private uploadFileToS3(
    file: File,
    preSignedUrl: string
  ): Observable<unknown> {
    let headers = new HttpHeaders({ 'Content-Type': file.type });
    if (environment.stage !== 'LOCAL') {
      headers = headers.set('x-amz-server-side-encryption', 'aws:kms');
    }
    // A plain Error, not an HttpErrorResponse: this request skips the error interceptor.
    return this.httpWithoutInterceptors
      .put(preSignedUrl, file, { headers })
      .pipe(
        catchError(() =>
          throwError(() => new Error(`S3 upload failed for ${file.name}`))
        )
      );
  }
}
